import React, { useContext, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Descriptions, Input, InputRef, Modal, Result } from 'antd'
import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import { handleResponseErrorMessage } from '../../../_utils/response-error-utils'
import { GetShopifyDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'

function getShopifyDomainName(domain: string) {
    return domain.replace(/\.myshopify\.com$/, '')
}

const ShopifyIntegration = (props: IntegrationProps<GetShopifyDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const shopNameInputRef = useRef<InputRef>()

    const svc = useService<DomainIntegrationService>(DomainIntegrationService)
    const history = useHistory()
    const { search: queryStringValue } = useLocation()

    const qs = queryString.parse(queryStringValue)

    React.useEffect(() => {
        const processOauthCallback = async (domainId: number, _oauthState: string) => {
            setPrimaryActionLoading(true)

            Modal.destroyAll()

            const modal = Modal.success({
                className: 'platform-integration-modal completing-oauth-dialog',
                title: 'Integration Finalizing',
                icon: <span className="shopify-logo-color" />,
                okText: 'Complete',
                cancelText: 'Cancel',
                cancelButtonProps: {
                    className: 'platform-integration-modal-action-cancel',
                    type: 'text',
                },
                footer: null,
                content: (
                    <div>
                        <Loading3QuartersOutlined spin={true} />
                    </div>
                ),
            } as any)

            const res = await svc.completeIntegrationSetup(domainId, DomainIntegrationProviders.SHOPIFY, qs, {
                cancellationKey: 'shopify-integration',
            })
            setPrimaryActionLoading(true)

            if (!res.ok || !res.data) {
                modal.update({
                    className: 'platform-integration-modal failed-oauth-dialog',
                    title: 'Integration Failed',
                    content: (
                        <Result
                            status="warning"
                            title={false}
                            subTitle="An error occurred during your integration and we were unable to complete the setup."
                        />
                    ),
                    onOk: () => {
                        history.replace({ hash: 'integrations', search: '' })
                        modal.destroy()
                    },
                })

                return
            }

            modal.update({
                className: 'platform-integration-modal completed-oauth-dialog',
                title: 'Integration Completed',
                content: (
                    <Result
                        status="success"
                        title={false}
                        subTitle={
                            <>
                                <p>
                                    The Platform Shopify app has been successfully installed on your store. Within the
                                    next 15 minutes visitors to your store will begin to be prompted to opt-in to web
                                    push notifications.
                                </p>
                                <p className="details">
                                    Your Shopify product catalog has been automatically imported to the platform and you
                                    may set up Abandonment Notifications via the Journeys section of the platform.
                                </p>
                            </>
                        }
                    />
                ),
                okText: 'Close',
                onOk: () => {
                    history.replace({ search: '', hash: 'integrations' })
                    modal.destroy()
                    simpleNotification('success', 'Integration successfully completed.')
                },
            })
        }

        if (mode === 'add' && domain?.id && qs.provider === DomainIntegrationProviders.SHOPIFY && qs.state) {
            processOauthCallback(domain.id, qs.state.toString())
        }
    }, [mode, domain?.id, qs.state, qs.provider])

    const createIntegration = () => {
        if (loading || mode !== 'add') return
        setPrimaryActionLoading(true)

        Modal.confirm({
            className: 'platform-integration-modal setup-oauth-dialog',
            title: 'Integration Setup',
            icon: <span className="shopify-logo-color" />,
            okText: 'Connect',
            cancelText: 'Cancel',
            cancelButtonProps: {
                className: 'platform-integration-modal-action-cancel cancel-btn',
                type: 'text',
            },
            content: (
                <div>
                    Shop address
                    <Input ref={shopNameInputRef as any} autoFocus={true} addonAfter={<span>.myshopify.com</span>} />
                </div>
            ),
            onOk: () => {
                return new Promise<void>(async (res, rej) => {
                    const inputRef = shopNameInputRef.current!.input
                    const v = inputRef?.value.trim()

                    if (!v) {
                        rej()
                    }

                    const redirectUrlRes = await svc.fetchIntegrationRedirectUrl('shopify', domain!.id, {
                        shop: `${v}.myshopify.com`,
                    })

                    if (!redirectUrlRes.ok) {
                        const err = redirectUrlRes.error as any
                        handleResponseErrorMessage(err)
                        rej()
                        return
                    }

                    const { authUrl } = redirectUrlRes.data!
                    window.location.href = authUrl
                    res()
                })
            },
            onCancel: () => {
                setPrimaryActionLoading(false)
            },
        })
    }

    const removeIntegration = async () => {
        if (loading || !integration) return
        setPrimaryActionLoading(true)

        Modal.confirm({
            className: 'platform-integration-modal remove-integration-dialog',
            title: 'Remove Integration',
            icon: <span className="shopify-logo-color" />,
            okText: 'Complete',
            okButtonProps: {
                className: 'platform-integration-modal-action-ok action-type-remove',
                type: 'text',
            },
            cancelText: 'Cancel',
            cancelButtonProps: {
                className: 'platform-integration-modal-action-cancel',
                type: 'text',
            },
            content: (
                <div>
                    <p>
                        Performing this step will remove the <b>{getShopifyDomainName(integration.data.shop)} </b>
                        integration and will stop syncing products from your store to the Pushly platform.
                    </p>
                    <p>
                        In order to completely disable the Pushly integration you will also need to navigate to your
                        Shopify Admin and remove the Pushly app.
                    </p>
                </div>
            ),
            onOk: async () => {
                const { ok } = await svc.removeIntegration(domain.id, integration.id)
                if (ok) {
                    setPrimaryActionLoading(false)
                    simpleNotification('success', 'Integration successfully removed.')
                    dispatch({ type: 'rem-integration', integration })
                }
            },
            onCancel: async () => {
                setPrimaryActionLoading(false)
            },
        })
    }

    return (
        <PlatformIntegrationCard
            loading={loading}
            className={props.className}
            provider={DomainIntegrationProviders.SHOPIFY}
            logo={<span className="shopify-logo-color" />}
            mode={mode}
            isActive={integration?.is_active}
            actions={{
                primary: {
                    loading: primaryActionLoading,
                    onClick: () => {
                        if (!integration) {
                            createIntegration()
                        } else {
                            removeIntegration()
                        }
                    },
                },
            }}
        >
            {integration && (
                <Descriptions layout="horizontal" column={1}>
                    <Descriptions.Item label="Shop">{getShopifyDomainName(integration.data.shop)}</Descriptions.Item>
                </Descriptions>
            )}
        </PlatformIntegrationCard>
    )
}

export default ShopifyIntegration
